import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { signupUser, api, authApi } from "../utils/api";
import { MEDICAL_SPECIALIZATIONS } from "../constants/specializations";
import { getLogoUrl } from "../utils/logoUtils";
import "./Signup.css";

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    role: "Patient",
    doctorDetails: {
      specialization: "",
      yearsOfExperience: 0,
      consultationFee: 0,
      education: [{ degree: "", institution: "", year: "" }],
      gender: "",
      bmdcRegistrationNumber: "",
      bmdcRegistrationCertificate: "",
    },
    driverDetails: {
      vehicleType: "standard",
      vehicleNumber: "",
      licenseNumber: "",
      licensePicture: "",
      carChassisNumber: "",
      carFitnessNumber: "",
      lastFitnessRenewalDate: "",
      lastTaxRenewalDate: "",
      ambulanceType: "",
      carModel: "",
      providingOrganization: "",
      yearsOfExperience: 0,
      location: "",
    },
  });
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [licensePreview, setLicensePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [logoUrl, setLogoUrl] = useState(getLogoUrl());

  // Initialize theme
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const darkMode = savedTheme === 'dark';
    setIsDarkMode(darkMode);
    document.documentElement.setAttribute('data-theme', savedTheme);
    setLogoUrl(getLogoUrl());
  }, []);

  // Update logo when theme changes
  React.useEffect(() => {
    setLogoUrl(getLogoUrl());
  }, [isDarkMode]);


  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    const theme = newDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    setLogoUrl(getLogoUrl());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("doctorDetails.")) {
      const field = name.split(".")[1];
      setFormData((prevData) => ({
        ...prevData,
        doctorDetails: {
          ...prevData.doctorDetails,
          [field]: value,
        },
      }));
    } else if (name.startsWith("driverDetails.")) {
      const field = name.split(".")[1];
      setFormData((prevData) => ({
        ...prevData,
        driverDetails: {
          ...prevData.driverDetails,
          [field]: value,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setCertificateFile(null);
      setCertificatePreview(null);
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setMessage("Invalid file type. Only JPEG and PNG images are allowed for BMDC certificate.");
      e.target.value = '';
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage("File size too large. Maximum size is 5MB.");
      e.target.value = '';
      return;
    }

    setCertificateFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCertificatePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLicenseChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setLicenseFile(null);
      setLicensePreview(null);
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setMessage("Invalid file type. Only JPEG and PNG images are allowed for license picture.");
      e.target.value = '';
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage("File size too large. Maximum size is 5MB.");
      e.target.value = '';
      return;
    }

    setLicenseFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLicensePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setMessage("Passwords do not match!");
      return;
    }

    if (!agreedToTerms) {
      setMessage("Please agree to the terms and conditions");
      return;
    }

    setIsLoading(true);
    setMessage("");
    
    try {
      // Upload certificate first if provided (for doctors)
      let certificateUrl = "";
      if (formData.role === "Doctor" && certificateFile) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('certificate', certificateFile);
          
          const uploadResponse = await authApi.post('/upload-certificate', uploadFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (uploadResponse.data.success && uploadResponse.data.url) {
            certificateUrl = uploadResponse.data.url;
          } else {
            throw new Error(uploadResponse.data.message || 'Failed to upload certificate');
          }
        } catch (uploadError) {
          console.error('Certificate upload error:', uploadError);
          setMessage(uploadError.response?.data?.message || "Failed to upload certificate. Please try again.");
          setIsLoading(false);
          return;
        }
      }

      // Upload license picture first if provided (for drivers)
      let licenseUrl = "";
      if (formData.role === "Driver" && licenseFile) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('license', licenseFile);
          
          const uploadResponse = await authApi.post('/upload-license', uploadFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (uploadResponse.data.success && uploadResponse.data.url) {
            licenseUrl = uploadResponse.data.url;
          } else {
            throw new Error(uploadResponse.data.message || 'Failed to upload license picture');
          }
        } catch (uploadError) {
          console.error('License upload error:', uploadError);
          setMessage(uploadError.response?.data?.message || "Failed to upload license picture. Please try again.");
          setIsLoading(false);
          return;
        }
      }

      // Prepare data based on role
      const submitData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        role: formData.role
      };

      // Only include role-specific details if the role requires them
      if (formData.role === "Doctor") {
        submitData.doctorDetails = {
          ...formData.doctorDetails,
          bmdcRegistrationCertificate: certificateUrl
        };
      } else if (formData.role === "Driver") {
        submitData.driverDetails = {
          ...formData.driverDetails,
          licensePicture: licenseUrl
        };
      }

      const response = await signupUser(submitData);
      if (response.success) {
        setMessage("Account created successfully! Please check your email for verification.");
        setTimeout(() => {
          navigate("/verify-email", { state: { email: formData.email } });
        }, 2000);
      } else {
        setMessage(response.message || "Failed to create account");
      }
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.message || "Error creating account. Please try again later.";
      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div 
      className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden my-auto signup-page" 
      style={{ 
        fontFamily: "'Inter', sans-serif",
        backgroundImage: 'url(https://res.cloudinary.com/dmmetrvda/image/upload/v1734707300/weheal-backgrounds/signup-background.jpg)',
        backgroundSize: '100% 100%',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Back to Home Button */}
      <Link
        to="/"
        className="fixed top-4 left-4 z-50 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 flex items-center gap-2 group"
        title="Back to Home"
        aria-label="Back to Home"
      >
        <svg className="w-5 h-5 text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors hidden sm:inline">Home</span>
      </Link>

      {/* Dark Mode Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-4 right-4 z-50 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 dark-mode-toggle-btn"
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-transparent signup-overlay"></div>
      
      {/* Content Container */}
      <div className="w-full max-w-2xl relative z-10">
        {/* Logo & Header */}
        <div className="text-center mb-3">
          <Link to="/" className="inline-block mb-2">
            <div className="inline-flex items-center gap-2">
              <img 
                src={logoUrl} 
                alt="WeHeal" 
                className={isDarkMode ? "h-8 w-auto" : "h-12 w-auto"}
                onError={(e) => {
                  // Fallback to text logo if image fails
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent && !parent.querySelector('.fallback-logo')) {
                    const fallback = document.createElement('div');
                    fallback.className = `fallback-logo ${isDarkMode ? 'w-8 h-8' : 'w-12 h-12'} bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-lg`;
                    fallback.innerHTML = '<span class="text-white text-lg font-bold">W</span>';
                    parent.insertBefore(fallback, e.target);
                  }
                }}
              />
            </div>
          </Link>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
            Create Your Account
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-xs">
            Join thousands accessing quality healthcare
          </p>
        </div>

        {/* Modern Glass Card */}
        <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700/50 p-4 signup-card">
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Personal Information */}
            <div>
              <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Personal Information
              </h4>
              
              <div className="grid md:grid-cols-2 gap-2">
                {/* Full Name */}
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                    Full Name *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-4 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-4 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Account Security */}
            <div>
              <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Account Security
              </h4>
              
              <div className="space-y-2">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                    Email Address *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-4 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-2">
                  {/* Password */}
                  <div>
                    <label htmlFor="password" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                      Password *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        required
                        value={formData.password}
                        onChange={handleChange}
                        className="appearance-none block w-full pl-10 pr-10 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        {showPassword ? (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 leading-tight">Min. 8 characters</p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        required
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="appearance-none block w-full pl-10 pr-10 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        {showConfirmPassword ? (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Account Type
              </h4>
              
              <div className="grid grid-cols-3 gap-1.5">
                {["Patient", "Doctor", "Nurse", "Driver", "ClinicStaff"].map((role) => (
                  <label
                    key={role}
                    className={`relative flex flex-col items-center p-2 border-2 rounded-lg cursor-pointer transition ${
                      formData.role === role
                        ? "border-blue-600 dark:border-blue-400 bg-blue-50/80 dark:bg-blue-900/30 backdrop-blur-sm"
                        : "border-gray-200/50 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white/30 dark:bg-gray-700/30 backdrop-blur-sm"
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role}
                      checked={formData.role === role}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-lg mb-1">
                      {role === "Patient" && "🧑‍⚕️"}
                      {role === "Doctor" && "👨‍⚕️"}
                      {role === "Nurse" && "👩‍⚕️"}
                      {role === "Driver" && "🚗"}
                      {role === "ClinicStaff" && "👥"}
                    </span>
                    <span className="text-xs font-medium text-gray-900 dark:text-white text-center">{role === "ClinicStaff" ? "Clinic Staff" : role}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Doctor Details */}
            {formData.role === "Doctor" && (
              <div className="bg-blue-50/80 dark:bg-blue-900/30 backdrop-blur-sm border border-blue-200/50 dark:border-blue-700/50 rounded-xl p-3">
                <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Professional Details</h4>
                <div className="grid md:grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="specialization" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Specialization *
                    </label>
                    <select
                      id="specialization"
                      name="doctorDetails.specialization"
                      required
                      value={formData.doctorDetails.specialization}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                    >
                      <option value="">Select Specialization</option>
                      {MEDICAL_SPECIALIZATIONS.map((spec) => (
                        <option key={spec} value={spec}>
                          {spec}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="gender" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Gender *
                    </label>
                    <select
                      id="gender"
                      name="doctorDetails.gender"
                      required
                      value={formData.doctorDetails.gender}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="yearsOfExperience" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Years of Experience *
                    </label>
                    <input
                      type="number"
                      id="yearsOfExperience"
                      name="doctorDetails.yearsOfExperience"
                      required
                      min="0"
                      value={formData.doctorDetails.yearsOfExperience}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="5"
                    />
                  </div>

                  <div>
                    <label htmlFor="consultationFee" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Consultation Fee ($) *
                    </label>
                    <input
                      type="number"
                      id="consultationFee"
                      name="doctorDetails.consultationFee"
                      required
                      min="0"
                      value={formData.doctorDetails.consultationFee}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="50"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="bmdcRegistrationNumber" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      BMDC Registration Number *
                    </label>
                    <input
                      type="text"
                      id="bmdcRegistrationNumber"
                      name="doctorDetails.bmdcRegistrationNumber"
                      required
                      value={formData.doctorDetails.bmdcRegistrationNumber}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="Enter your BMDC registration number"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="bmdcCertificate" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      BMDC Registration Certificate (Optional)
                    </label>
                    <div className="space-y-2">
                      <input
                        type="file"
                        id="bmdcCertificate"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleCertificateChange}
                        className="appearance-none block w-full px-4 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-300"
                      />
                      {certificatePreview && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Preview:</p>
                          <img 
                            src={certificatePreview} 
                            alt="Certificate preview" 
                            className="max-w-full h-auto max-h-48 rounded-lg border border-gray-300 dark:border-gray-600"
                          />
                        </div>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400">Accepted formats: JPEG, PNG (Max 5MB)</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Driver Details */}
            {formData.role === "Driver" && (
              <div className="bg-green-50/80 dark:bg-green-900/30 backdrop-blur-sm border border-green-200/50 dark:border-green-700/50 rounded-xl p-3">
                <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Driver & Vehicle Information</h4>
                <div className="grid md:grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="licenseNumber" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      License Number *
                    </label>
                    <input
                      type="text"
                      id="licenseNumber"
                      name="driverDetails.licenseNumber"
                      required
                      value={formData.driverDetails.licenseNumber}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="Enter license number"
                    />
                  </div>

                  <div>
                    <label htmlFor="ambulanceType" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Ambulance Type *
                    </label>
                    <select
                      id="ambulanceType"
                      name="driverDetails.ambulanceType"
                      required
                      value={formData.driverDetails.ambulanceType}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                    >
                      <option value="">Select Ambulance Type</option>
                      <option value="Basic">Basic (500 BDT)</option>
                      <option value="Premium">Premium (2000 BDT)</option>
                      <option value="ICU">ICU (5000 BDT)</option>
                      <option value="NICU">NICU (6000 BDT)</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="vehicleNumber" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Car Number *
                    </label>
                    <input
                      type="text"
                      id="vehicleNumber"
                      name="driverDetails.vehicleNumber"
                      required
                      value={formData.driverDetails.vehicleNumber}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="Enter car number"
                    />
                  </div>

                  <div>
                    <label htmlFor="carModel" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Car Model *
                    </label>
                    <input
                      type="text"
                      id="carModel"
                      name="driverDetails.carModel"
                      required
                      value={formData.driverDetails.carModel}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="Enter car model"
                    />
                  </div>

                  <div>
                    <label htmlFor="carChassisNumber" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Car Chassis Number *
                    </label>
                    <input
                      type="text"
                      id="carChassisNumber"
                      name="driverDetails.carChassisNumber"
                      required
                      value={formData.driverDetails.carChassisNumber}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="Enter chassis number"
                    />
                  </div>

                  <div>
                    <label htmlFor="carFitnessNumber" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Car Fitness Number *
                    </label>
                    <input
                      type="text"
                      id="carFitnessNumber"
                      name="driverDetails.carFitnessNumber"
                      required
                      value={formData.driverDetails.carFitnessNumber}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="Enter fitness number"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastFitnessRenewalDate" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Last Fitness Renewal Date *
                    </label>
                    <input
                      type="date"
                      id="lastFitnessRenewalDate"
                      name="driverDetails.lastFitnessRenewalDate"
                      required
                      value={formData.driverDetails.lastFitnessRenewalDate}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastTaxRenewalDate" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Last Tax Renewal Date *
                    </label>
                    <input
                      type="date"
                      id="lastTaxRenewalDate"
                      name="driverDetails.lastTaxRenewalDate"
                      required
                      value={formData.driverDetails.lastTaxRenewalDate}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="providingOrganization" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      Providing Organization (Optional)
                    </label>
                    <input
                      type="text"
                      id="providingOrganization"
                      name="driverDetails.providingOrganization"
                      value={formData.driverDetails.providingOrganization}
                      onChange={handleChange}
                      className="appearance-none block w-full px-4 py-2.5 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm"
                      placeholder="Enter organization name (optional)"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="driverLicense" className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                      License Picture *
                    </label>
                    <div className="space-y-2">
                      <input
                        type="file"
                        id="driverLicense"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleLicenseChange}
                        required
                        className="appearance-none block w-full px-4 py-2 border-2 border-gray-300/50 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm text-gray-900 dark:text-white text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 dark:file:bg-green-900/30 dark:file:text-green-300"
                      />
                      {licensePreview && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Preview:</p>
                          <img 
                            src={licensePreview} 
                            alt="License preview" 
                            className="max-w-full h-auto max-h-48 rounded-lg border border-gray-300 dark:border-gray-600"
                          />
                        </div>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400">Accepted formats: JPEG, PNG (Max 5MB)</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Terms and Conditions */}
            <div className="bg-gray-50/80 dark:bg-gray-700/50 backdrop-blur-sm rounded-lg p-2 border border-gray-200/50 dark:border-gray-600/50">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                />
                <span className="text-xs text-gray-700 dark:text-gray-200">
                  I agree to the{" "}
                  <Link to="/terms" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                    Privacy Policy
                  </Link>
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading || !agreedToTerms}
                className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-lg shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating your account...
                  </>
                ) : (
                  <>
                    Create Account
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>

        </div>

        {/* Sign In Link */}
        <div className="text-center pt-2">
          <p className="text-xs text-gray-600 dark:text-gray-300">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
              Sign in →
            </Link>
          </p>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-2">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to home
          </Link>
        </div>
      </div>

      {/* Toast Message */}
      {message && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in-up">
          <div className={`rounded-lg shadow-xl px-6 py-4 max-w-sm ${
            message.includes("success") || message.includes("successful")
              ? "bg-green-50 border border-green-200"
              : "bg-red-50 border border-red-200"
          }`}>
            <div className="flex items-start gap-3">
              {message.includes("success") || message.includes("successful") ? (
                <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <p className={`text-sm font-medium ${
                message.includes("success") || message.includes("successful")
                  ? "text-green-800"
                  : "text-red-800"
              }`}>
                {message}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Signup;
